/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage */
import React, { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, type XCSS, xcss } from '@atlaskit/primitives';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { ResultsStates } from '../../../common/constants/results';
import { useAppContext } from '../../../common/ui/app-context';
import { BrowserExtensionBanner } from '../../../common/ui/browser-extension-banner';
import { SidebarContainer } from '../../../common/ui/filters/app-filter-sidebar/sidebar-container';
import { SidebarLoading } from '../../../common/ui/filters/app-filter-sidebar/sidebar-loading';
import { isBreakpointActive, useElementWidth } from '../../../common/ui/width-observer';
import { useAvailableProducts } from '../../../controllers/store/bootstrap';
import { useResultsState } from '../../../controllers/store/search';

import AppFilter from './app-filter';
import { messages } from './messages';

const baseStyles = xcss({});

export const RightPanelSkeleton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<SidebarContainer
			ariaLabel={formatMessage(messages.groupLabel)}
			data-testid="app-filter-sidebar-loading"
		>
			<SidebarLoading />
		</SidebarContainer>
	);
};

const RightPanel = (): React.JSX.Element => {
	const [stickyPanelStyles, setStickyPanelStyles] = useState<XCSS>();
	const referenceRef = useRef<HTMLDivElement>(null);
	const [topReference, setTopReference] = useState<number>(0);
	const layoutWidth = useElementWidth('layout');
	const isAboveSmall = isBreakpointActive({ width: layoutWidth, above: 'sm' });
	const { isNav4Enabled } = useAppContext();
	const availableProducts = useAvailableProducts();

	// TODO: instead of the results state we should track results count state.
	// As part of this we should review the behaviour of useProductResultCountMap() which returns null when the app filter changes.
	// This is to prevent a bug where the right rail re-load when the app filter is change (even if the query remains the same).
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const resultsState = useResultsState();

	// Use the reference element's position to set the sticky styles on the
	// header. This allows `top` to always be correct.
	useEffect(() => {
		if (!referenceRef.current) {
			return;
		}

		// QS-5148
		// referenceY is a measure of how far the top of search-page is from the top of the scrolling context. This works in old nav, because the scroll matches the viewport
		// In Nav 4, the scroll is the content region and the nav is outside of it
		// This is a temporary fix to unblock Nav4 dogfooding
		let referenceY = referenceRef.current.getBoundingClientRect().top;
		if (fg('confluence_nav_4')) {
			referenceY = referenceRef.current.offsetTop;
		}

		// If the Nav4 feature prop is enabled, we set the referenceY to 0 as the layout doesn't require an offset
		if (isNav4Enabled) {
			referenceY = 0;
		}

		const scrollY = window.scrollY;
		const top = referenceY + scrollY;
		setTopReference(top);

		setStickyPanelStyles(
			xcss({
				position: 'sticky',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				top: `${top}px`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			}),
		);
		// By including isAboveSmall in the dependency array, we force the sticky panel to resize when the breakpoint changes.
		// This fixes a small bug where the sticky panel doesn't resize on breakpoint change.
	}, [setTopReference, setStickyPanelStyles, isAboveSmall, isNav4Enabled]);

	return (
		<>
			<Box ref={referenceRef} testId="search-page-sticky-reference" />
			<Box xcss={[baseStyles, stickyPanelStyles]}>
				{availableProducts.length === 0 ||
				(resultsState === ResultsStates.IS_LOADING &&
					fg('rovo-search-right-rail-personalisation')) ? (
					<UFOLoadHold name="search-page-right-panel-loading">
						<RightPanelSkeleton />
					</UFOLoadHold>
				) : (
					<UFOSegment name="search-page-right-panel">
						<BrowserExtensionBanner />
						<AppFilter top={topReference} isScrollable />
					</UFOSegment>
				)}
			</Box>
		</>
	);
};

export default RightPanel;
